import { gql } from '@apollo/client/core'
import { useMutation } from '/@/core/graphql'
import {
  type ProjectBudget,
  type ProjectBudgetInput,
  type ProjectThingBudget,
  type ProjectThingBudgetInput,
  type ProjectThingBudgetStatus,
  type ProjectThingBudgetStatusChange,
  type SectionBudget,
  type SectionBudgetInput,
} from './types'
import type { ProjectThingType } from '/@/common/things/types'
import {
  projectBudgetFragment,
  projectThingBudgetStatusChangeFragment,
  projectThingBudgetFragment,
  sectionBudgetFragment,
} from './fragments'
import { useProjectThingBudgetCache } from './utils'
import { useProjectCache, useProjectThingCache } from '/@/common/projects/utils'

export function useBudgetApi() {
  const projectCache = useProjectCache()
  const projectThingCache = useProjectThingCache()
  const projectThingBudgetCache = useProjectThingBudgetCache()

  const createBudgetMutation = gql`
    ${projectBudgetFragment}
    mutation createBudget($projectId: String!, $input: ProjectBudgetInput!) {
      project {
        budget {
          create(projectId: $projectId, input: $input) {
            status
            recordId
            record {
              ...ProjectBudgetProps
            }
          }
        }
      }
    }
  `
  const updateBudgetMutation = gql`
    ${projectBudgetFragment}
    mutation updateBudget($id: String!, $input: ProjectBudgetInput!) {
      project {
        budget {
          update(id: $id, input: $input) {
            status
            recordId
            record {
              ...ProjectBudgetProps
            }
          }
        }
      }
    }
  `
  const deleteBudgetMutation = gql`
    ${projectBudgetFragment}
    mutation deleteBudget($id: String!) {
      project {
        budget {
          delete(id: $id) {
            status
            recordId
            record {
              ...ProjectBudgetProps
            }
          }
        }
      }
    }
  `
  const createSectionBudgetMutation = gql`
    ${sectionBudgetFragment}
    mutation createSectionBudget(
      $sectionId: String!
      $input: SectionBudgetInput!
    ) {
      project {
        section {
          budget {
            create(sectionId: $sectionId, input: $input) {
              status
              recordId
              record {
                ...SectionBudgetProps
              }
            }
          }
        }
      }
    }
  `
  const updateSectionBudgetMutation = gql`
    ${sectionBudgetFragment}
    mutation updateSectionBudget($id: String!, $input: SectionBudgetInput!) {
      project {
        section {
          budget {
            update(id: $id, input: $input) {
              status
              recordId
              record {
                ...SectionBudgetProps
              }
            }
          }
        }
      }
    }
  `
  const deleteSectionBudgetMutation = gql`
    ${sectionBudgetFragment}
    mutation deleteSectionBudget($id: String!) {
      project {
        section {
          budget {
            delete(id: $id) {
              status
              recordId
              record {
                ...SectionBudgetProps
              }
            }
          }
        }
      }
    }
  `
  const createBudgetItemMutation = gql`
    ${projectThingBudgetFragment}
    mutation createBudgetItem(
      $projectThingId: String!
      $projectThingType: ProjectThingType!
      $status: ProjectThingBudgetStatus
      $input: ProjectThingBudgetInput!
    ) {
      project {
        thing {
          budget {
            add(
              projectThingId: $projectThingId
              projectThingType: $projectThingType
              status: $status
              input: $input
            ) {
              status
              record {
                ...ProjectThingBudgetProps
              }
              recordId
            }
          }
        }
      }
    }
  `
  const addProductToBudgetMutation = gql`
    ${projectThingBudgetFragment}
    mutation addProductToBudget(
      $projectId: String!
      $productId: String!
      $skuId: String
      $input: ProjectThingBudgetInput!
    ) {
      project {
        thing {
          budget {
            addProduct(
              projectId: $projectId
              productId: $productId
              skuId: $skuId
              input: $input
            ) {
              status
              record {
                ...ProjectThingBudgetProps
              }
              recordId
            }
          }
        }
      }
    }
  `
  const updateBudgetItemMutation = gql`
    ${projectThingBudgetFragment}
    mutation updateBudgetItem($id: String!, $input: ProjectThingBudgetInput!) {
      project {
        thing {
          budget {
            update(id: $id, input: $input) {
              status
              record {
                ...ProjectThingBudgetProps
              }
              recordId
            }
          }
        }
      }
    }
  `
  const deleteBudgetItemMutation = gql`
    mutation deleteBudgetItem($id: String!) {
      project {
        thing {
          budget {
            delete(id: $id) {
              status
              recordId
            }
          }
        }
      }
    }
  `
  const createBudgetItemStatusMutation = gql`
    ${projectThingBudgetStatusChangeFragment}
    mutation createBudgetItemStatus(
      $projectThingBudgetId: String!
      $input: ProjectThingBudgetStatusChangeInput!
    ) {
      project {
        thing {
          budget {
            statusChanges {
              create(
                projectThingBudgetId: $projectThingBudgetId
                input: $input
              ) {
                status
                record {
                  ...ProjectThingBudgetStatusChangeProps
                }
                recordId
              }
            }
          }
        }
      }
    }
  `

  const { mutate: createBudget } = useMutation(createBudgetMutation)
  const { mutate: updateBudget } = useMutation(updateBudgetMutation)
  const { mutate: deleteBudget } = useMutation(deleteBudgetMutation)
  const { mutate: createSectionBudget } = useMutation(
    createSectionBudgetMutation,
  )
  const { mutate: updateSectionBudget } = useMutation(
    updateSectionBudgetMutation,
  )
  const { mutate: deleteSectionBudget } = useMutation(
    deleteSectionBudgetMutation,
  )
  const { mutate: createBudgetItem } = useMutation(createBudgetItemMutation)
  const { mutate: addProductToBudget } = useMutation(addProductToBudgetMutation)
  const { mutate: updateBudgetItem } = useMutation(updateBudgetItemMutation)
  const { mutate: deleteBudgetItem } = useMutation(deleteBudgetItemMutation)
  const { mutate: createBudgetItemStatus } = useMutation(
    createBudgetItemStatusMutation,
  )

  return {
    async createBudget(projectId: string, input: ProjectBudgetInput) {
      await createBudget(
        {
          projectId,
          input,
        },
        {
          update(
            cache,
            {
              data: {
                project: {
                  budget: {
                    create: { record },
                  },
                },
              },
            },
          ) {
            const budget = record as ProjectBudget
            const newBudgetRef = cache.writeFragment({
              data: budget,
              fragmentName: 'NewProjectBudget',
              fragment: gql`
                ${projectBudgetFragment}
                fragment NewProjectBudget on ProjectBudget {
                  ...ProjectBudgetProps
                }
              `,
            })
            if (!newBudgetRef) return
            projectCache.addProjectBudget(cache, newBudgetRef, projectId)
          },
        },
      )
    },
    async updateBudget(id: string, input: ProjectBudgetInput) {
      await updateBudget({
        id,
        input,
      })
    },
    async deleteBudget(id: string, projectId: string) {
      await deleteBudget(
        {
          id,
        },
        {
          update(cache) {
            projectCache.deleteProjectBudget(cache, projectId)
          },
        },
      )
    },
    async createSectionBudget(
      sectionId: string,
      projectId: string,
      input: SectionBudgetInput,
    ) {
      await createSectionBudget(
        {
          sectionId,
          input,
        },
        {
          update(
            cache,
            {
              data: {
                project: {
                  section: {
                    budget: {
                      create: { record },
                    },
                  },
                },
              },
            },
          ) {
            const budget = record as SectionBudget
            const newBudgetRef = cache.writeFragment({
              data: budget,
              fragmentName: 'NewSectionBudget',
              fragment: gql`
                ${sectionBudgetFragment}
                fragment NewSectionBudget on SectionBudget {
                  ...SectionBudgetProps
                }
              `,
            })
            if (!newBudgetRef) return
            projectCache.addSectionBudget(
              cache,
              newBudgetRef,
              projectId,
              sectionId,
            )
          },
        },
      )
    },
    async updateSectionBudget(id: string, input: SectionBudgetInput) {
      await updateSectionBudget({
        id,
        input,
      })
    },
    async deleteSectionBudget(
      id: string,
      projectId: string,
      sectionId: string,
    ) {
      await deleteSectionBudget(
        {
          id,
        },
        {
          update(cache) {
            projectCache.deleteSectionBudget(cache, projectId, sectionId)
          },
        },
      )
    },
    async createBudgetItem(
      projectId: string,
      projectThingId: string,
      projectThingType: ProjectThingType,
      input: ProjectThingBudgetInput,
      status?: ProjectThingBudgetStatus,
    ) {
      await createBudgetItem(
        {
          projectThingId,
          projectThingType,
          input,
          status,
        },
        {
          update(
            cache,
            {
              data: {
                project: {
                  thing: {
                    budget: {
                      add: { record },
                    },
                  },
                },
              },
            },
          ) {
            const budget = record as ProjectThingBudget
            const newBudgetRef = cache.writeFragment({
              data: budget,
              fragmentName: 'NewProjectThingBudget',
              fragment: gql`
                ${projectThingBudgetFragment}
                fragment NewProjectThingBudget on ProjectThingBudget {
                  ...ProjectThingBudgetProps
                }
              `,
            })
            if (!newBudgetRef) return

            projectThingCache.addProjectThingBudget(
              cache,
              newBudgetRef,
              projectId,
              projectThingId,
              projectThingType,
            )
          },
        },
      )
    },
    async addProductToBudget(
      projectId: string,
      productId: string,
      input: ProjectThingBudgetInput,
      skuId?: string,
    ) {
      await addProductToBudget(
        {
          projectId,
          productId,
          skuId,
          input,
        },
        {
          update(
            cache,
            {
              data: {
                project: {
                  thing: {
                    budget: {
                      addProduct: { record },
                    },
                  },
                },
              },
            },
          ) {
            const budget = record as ProjectThingBudget
            const newBudgetRef = cache.writeFragment({
              data: budget,
              fragmentName: 'NewProjectThingBudget',
              fragment: gql`
                ${projectThingBudgetFragment}
                fragment NewProjectThingBudget on ProjectThingBudget {
                  ...ProjectThingBudgetProps
                }
              `,
            })
            if (!newBudgetRef) return

            // fixme: hm, how do we handle this cache update? maybe the api should return
            // a ProjectProduct record
            // projectThingCache.addProjectThingBudget(
            //   cache,
            //   newBudgetRef,
            //   projectId,
            //   projectThingId,
            //   projectThingType,
            // )
          },
        },
      )
    },
    async updateBudgetItem(id: string, input: ProjectThingBudgetInput) {
      await updateBudgetItem({
        id,
        input,
      })
    },
    async deleteBudgetItem(
      id: string,
      projectId: string,
      projectThingId: string,
      projectThingType: ProjectThingType,
    ) {
      await deleteBudgetItem(
        {
          id,
        },
        {
          update(cache) {
            projectThingCache.deleteProjectThingBudget(
              cache,
              projectId,
              projectThingId,
              projectThingType,
            )
          },
        },
      )
    },
    async createBudgetItemStatus(
      projectId: string,
      projectThingBudgetId: string,
      status: ProjectThingBudgetStatus,
    ) {
      await createBudgetItemStatus(
        {
          projectThingBudgetId,
          input: { status },
        },
        {
          update(
            cache,
            {
              data: {
                project: {
                  thing: {
                    budget: {
                      statusChanges: {
                        create: { record },
                      },
                    },
                  },
                },
              },
            },
          ) {
            const statusChange = record as ProjectThingBudgetStatusChange
            const newStatusChangeRef = cache.writeFragment({
              data: statusChange,
              fragmentName: 'NewProjectThingBudgetStatusChange',
              fragment: gql`
                ${projectThingBudgetStatusChangeFragment}
                fragment NewProjectThingBudgetStatusChange on ProjectThingBudgetStatusChange {
                  ...ProjectThingBudgetStatusChangeProps
                }
              `,
            })
            if (!newStatusChangeRef) return

            projectThingBudgetCache.addListItem(
              cache,
              'statusChanges',
              newStatusChangeRef,
              projectId,
              projectThingBudgetId,
              { insertMode: 'prepend' },
            )
          },
        },
      )
    },
  }
}
